import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline'
import { Dialog } from '@headlessui/react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { OAuthClient, Webhook } from '@/types'
import { fetchOAuthClients, createOAuthClient, fetchWebhooks, createWebhook, deleteWebhook } from '@/services/api'

const mockOAuthClients: OAuthClient[] = [
  {
    client_id: 'client_abc123',
    name: 'Mobile App',
    description: 'iOS and Android mobile application',
    redirect_uris: ['https://app.example.com/callback'],
    scopes: ['read', 'write'],
    created_at: '2023-12-01T10:00:00Z'
  }
]

const mockWebhooks: Webhook[] = [
  {
    id: '1',
    url: 'https://api.partner.com/webhooks',
    events: ['user.created', 'user.updated'],
    secret: 'whsec_abc123',
    active: true,
    created_at: '2023-12-01T10:00:00Z',
    last_triggered: '2023-12-10T15:30:00Z'
  }
]

interface OAuthClientForm {
  name: string
  description: string
  redirect_uris: string
  scopes: string[]
}

interface WebhookForm {
  url: string
  events: string[]
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState<'oauth' | 'webhooks' | 'general'>('oauth')
  const [isOAuthModalOpen, setIsOAuthModalOpen] = useState(false)
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false)
  const queryClient = useQueryClient()

  const { data: oauthClients = mockOAuthClients } = useQuery({
    queryKey: ['oauthClients'],
    queryFn: fetchOAuthClients,
  })

  const { data: webhooks = mockWebhooks } = useQuery({
    queryKey: ['webhooks'],
    queryFn: fetchWebhooks,
  })

  const createOAuthMutation = useMutation({
    mutationFn: createOAuthClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauthClients'] })
      setIsOAuthModalOpen(false)
      toast.success('OAuth client created successfully')
    },
    onError: () => {
      toast.error('Failed to create OAuth client')
    }
  })

  const createWebhookMutation = useMutation({
    mutationFn: createWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      setIsWebhookModalOpen(false)
      toast.success('Webhook created successfully')
    },
    onError: () => {
      toast.error('Failed to create webhook')
    }
  })

  const deleteWebhookMutation = useMutation({
    mutationFn: deleteWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      toast.success('Webhook deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete webhook')
    }
  })

  const oauthForm = useForm<OAuthClientForm>({
    defaultValues: {
      scopes: ['read']
    }
  })

  const webhookForm = useForm<WebhookForm>({
    defaultValues: {
      events: ['user.created']
    }
  })

  const onSubmitOAuth = (data: OAuthClientForm) => {
    createOAuthMutation.mutate({
      name: data.name,
      description: data.description,
      redirect_uris: data.redirect_uris.split('\n').filter(uri => uri.trim()),
      scopes: data.scopes
    })
  }

  const onSubmitWebhook = (data: WebhookForm) => {
    createWebhookMutation.mutate({
      url: data.url,
      events: data.events
    })
  }

  const availableEvents = [
    'user.created',
    'user.updated',
    'user.deleted',
    'order.created',
    'order.updated',
    'payment.completed'
  ]

  const tabs = [
    { id: 'oauth', name: 'OAuth Apps', description: 'Manage third-party application access' },
    { id: 'webhooks', name: 'Webhooks', description: 'Configure real-time event notifications' },
    { id: 'general', name: 'General', description: 'API settings and preferences' }
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-sm text-gray-700">
          Configure your API platform settings and integrations
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* OAuth Apps Tab */}
      {activeTab === 'oauth' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-900">OAuth Applications</h2>
              <p className="text-sm text-gray-500">
                Register applications that can access your API on behalf of users
              </p>
            </div>
            <button
              onClick={() => setIsOAuthModalOpen(true)}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New OAuth App
            </button>
          </div>

          <div className="card">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Application
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Client ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Scopes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {oauthClients.map((client) => (
                    <tr key={client.client_id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{client.name}</div>
                          <div className="text-sm text-gray-500">{client.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {client.client_id}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-1">
                          {client.scopes.map((scope) => (
                            <span
                              key={scope}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {scope}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(client.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button className="text-primary-600 hover:text-primary-900 mr-3">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Webhooks</h2>
              <p className="text-sm text-gray-500">
                Receive real-time notifications when events occur in your API
              </p>
            </div>
            <button
              onClick={() => setIsWebhookModalOpen(true)}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Webhook
            </button>
          </div>

          <div className="card">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Events
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Last Triggered
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {webhooks.map((webhook) => (
                    <tr key={webhook.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {webhook.url}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {webhook.events.map((event) => (
                            <span
                              key={event}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                            >
                              {event}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          webhook.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {webhook.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {webhook.last_triggered 
                          ? new Date(webhook.last_triggered).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button className="text-primary-600 hover:text-primary-900 mr-3">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => deleteWebhookMutation.mutate(webhook.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900">General Settings</h2>
            <p className="text-sm text-gray-500">
              Configure general API platform preferences
            </p>
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">API Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="label">Base URL</label>
                <input
                  type="text"
                  className="input"
                  value="https://api.yourdomain.com"
                  readOnly
                />
                <p className="mt-1 text-sm text-gray-500">
                  This is your API's base URL. Contact support to change this.
                </p>
              </div>
              
              <div>
                <label className="label">Default Rate Limit</label>
                <select className="input">
                  <option>1000 requests/hour</option>
                  <option>5000 requests/hour</option>
                  <option>10000 requests/hour</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  defaultChecked
                />
                <label className="ml-2 text-sm text-gray-700">
                  Enable CORS for browser requests
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  defaultChecked
                />
                <label className="ml-2 text-sm text-gray-700">
                  Log all API requests for debugging
                </label>
              </div>
            </div>

            <div className="mt-6">
              <button className="btn-primary">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* OAuth Client Modal */}
      <Dialog
        open={isOAuthModalOpen}
        onClose={() => setIsOAuthModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/25" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md rounded bg-white p-6 shadow-lg">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
              Create OAuth Application
            </Dialog.Title>

            <form onSubmit={oauthForm.handleSubmit(onSubmitOAuth)} className="space-y-4">
              <div>
                <label className="label">Application Name</label>
                <input
                  {...oauthForm.register('name', { required: true })}
                  className="input"
                  placeholder="My App"
                />
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  {...oauthForm.register('description')}
                  className="input"
                  rows={3}
                  placeholder="Brief description of your application"
                />
              </div>

              <div>
                <label className="label">Redirect URIs (one per line)</label>
                <textarea
                  {...oauthForm.register('redirect_uris', { required: true })}
                  className="input"
                  rows={3}
                  placeholder="https://yourapp.com/callback"
                />
              </div>

              <div>
                <label className="label">Scopes</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      value="read"
                      {...oauthForm.register('scopes')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Read</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      value="write"
                      {...oauthForm.register('scopes')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Write</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsOAuthModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createOAuthMutation.isPending}
                  className="btn-primary"
                >
                  {createOAuthMutation.isPending ? 'Creating...' : 'Create Application'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Webhook Modal */}
      <Dialog
        open={isWebhookModalOpen}
        onClose={() => setIsWebhookModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/25" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md rounded bg-white p-6 shadow-lg">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
              Add Webhook
            </Dialog.Title>

            <form onSubmit={webhookForm.handleSubmit(onSubmitWebhook)} className="space-y-4">
              <div>
                <label className="label">Webhook URL</label>
                <input
                  {...webhookForm.register('url', { required: true })}
                  className="input"
                  placeholder="https://yourapp.com/webhooks"
                />
              </div>

              <div>
                <label className="label">Events</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {availableEvents.map((event) => (
                    <label key={event} className="flex items-center">
                      <input
                        type="checkbox"
                        value={event}
                        {...webhookForm.register('events')}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{event}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsWebhookModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createWebhookMutation.isPending}
                  className="btn-primary"
                >
                  {createWebhookMutation.isPending ? 'Creating...' : 'Add Webhook'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  )
}
